/**
 * หน้าต่างระบบเทรดตรง (Direct Trade) ระหว่างผู้เล่น 2 คน
 *
 * UI สองฝั่ง: ของเรา vs ของคู่เทรด
 * ล็อก 2 ขั้น (Lock -> Confirm) ตามกฎ server-authoritative
 */

import {
  ITEMS,
  type ItemDef,
  type TradeOffer,
  type TradeSessionData,
} from "@wickhaven/shared";
import type { GameWindow } from "./windows.js";

export interface TradeViewCallbacks {
  onSetGold: (gold: number) => void;
  onOfferItem: (itemId: string, count: number) => void;
  onLock: (locked: boolean) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

export class TradeView {
  private session: TradeSessionData | null = null;
  private myId = "";
  private partnerId = "";
  private partnerName = "";
  private note = "";

  constructor(
    private win: GameWindow,
    private callbacks: TradeViewCallbacks,
  ) {}

  startSession(myId: string, partnerId: string, partnerName: string, session: TradeSessionData): void {
    this.myId = myId;
    this.partnerId = partnerId;
    this.partnerName = partnerName;
    this.session = session;
    this.note = "ตรวจสอบข้อเสนอและกด 'ล็อกข้อเสนอ' เมื่อพร้อม";
    this.win.open();
    this.render();
  }

  updateSession(session: TradeSessionData): void {
    this.session = session;
    this.render();
  }

  setNote(note: string): void {
    this.note = note;
    this.render();
  }

  close(): void {
    this.session = null;
    this.myId = "";
    this.partnerId = "";
    this.partnerName = "";
    this.note = "";
    this.win.close();
  }

  isOpen(): boolean {
    return this.win.isOpen();
  }

  render(myInventory?: Readonly<Record<string, number>>): void {
    const body = this.win.body;
    body.innerHTML = "";
    if (!this.session) {
      body.textContent = "ไม่มีการแลกเปลี่ยนในขณะนี้";
      return;
    }

    const isA = this.session.playerA === this.myId;
    const myOffer = isA ? this.session.offerA : this.session.offerB;
    const partnerOffer = isA ? this.session.offerB : this.session.offerA;

    const wrap = document.createElement("div");
    wrap.style.cssText = "display: flex; flex-direction: column; gap: 8px; width: 340px; font-size: 11px;";

    // สถานะแจ้งเตือน
    const noteEl = document.createElement("div");
    noteEl.style.cssText = "padding: 4px 6px; background: rgba(255, 200, 50, 0.1); border: 1px solid rgba(255, 200, 50, 0.3); border-radius: 4px; color: #f5cf6d; font-size: 10px;";
    noteEl.textContent = this.note;
    wrap.appendChild(noteEl);

    // 2 ฝั่ง (เรา vs คู่เทรด)
    const cols = document.createElement("div");
    cols.style.cssText = "display: grid; grid-template-columns: 1fr 1fr; gap: 8px;";

    // ฝั่งเรา
    const myCol = this.renderOfferColumn("ข้อเสนอของคุณ", myOffer, true);
    // ฝั่งคู่เทรด
    const partnerCol = this.renderOfferColumn(`ของ ${this.partnerName}`, partnerOffer, false);

    cols.appendChild(myCol);
    cols.appendChild(partnerCol);
    wrap.appendChild(cols);

    // ส่วนเพิ่มไอเทม (ถ้ายังไม่ล็อก)
    if (!myOffer.locked && myInventory) {
      const addSection = document.createElement("div");
      addSection.style.cssText = "border-top: 1px solid rgba(255,255,255,0.1); padding-top: 6px; display: flex; flex-direction: column; gap: 4px;";
      const addTitle = document.createElement("div");
      addTitle.style.cssText = "font-weight: bold; color: #ddd; font-size: 10px;";
      addTitle.textContent = "คลิกไอเทมจากกระเป๋า หรือเลือกจากรายการเพื่อเสนอ:";
      addSection.appendChild(addTitle);

      const selectWrap = document.createElement("div");
      selectWrap.style.cssText = "display: flex; gap: 4px; align-items: center;";
      const sel = document.createElement("select");
      sel.style.cssText = "flex: 1; background: #1a2233; color: #fff; border: 1px solid rgba(255,255,255,0.2); border-radius: 4px; padding: 2px 4px; font-size: 10px;";

      const defaultOpt = document.createElement("option");
      defaultOpt.value = "";
      defaultOpt.textContent = "-- เลือกไอเทมในกระเป๋า --";
      sel.appendChild(defaultOpt);

      for (const [id, count] of Object.entries(myInventory)) {
        if (count > 0) {
          const itemDef = ITEMS[id];
          const offeredCount = myOffer.items[id] ?? 0;
          const available = count - offeredCount;
          if (available > 0) {
            const opt = document.createElement("option");
            opt.value = id;
            opt.textContent = `${itemDef?.icon ?? "📦"} ${itemDef?.name ?? id} (มี ${available})`;
            sel.appendChild(opt);
          }
        }
      }

      const countInput = document.createElement("input");
      countInput.type = "number";
      countInput.min = "1";
      countInput.value = "1";
      countInput.style.cssText = "width: 45px; background: #1a2233; color: #fff; border: 1px solid rgba(255,255,255,0.2); border-radius: 4px; padding: 2px 4px; font-size: 10px;";

      const addBtn = document.createElement("button");
      addBtn.type = "button";
      addBtn.textContent = "+ เสนอ";
      addBtn.style.cssText = "padding: 2px 8px; background: #2f5485; border: 1px solid #4872ab; border-radius: 4px; color: #fff; cursor: pointer; font-size: 10px;";
      addBtn.onclick = () => {
        if (sel.value) {
          const c = parseInt(countInput.value, 10) || 1;
          const cur = myOffer.items[sel.value] ?? 0;
          this.callbacks.onOfferItem(sel.value, cur + c);
        }
      };

      selectWrap.appendChild(sel);
      selectWrap.appendChild(countInput);
      selectWrap.appendChild(addBtn);
      addSection.appendChild(selectWrap);
      wrap.appendChild(addSection);
    }

    // ปุ่มควบคุมหลัก (Lock / Confirm / Cancel)
    const ctrlBar = document.createElement("div");
    ctrlBar.style.cssText = "display: flex; gap: 6px; justify-content: flex-end; border-top: 1px solid rgba(255,255,255,0.1); padding-top: 8px;";

    const cancelBtn = document.createElement("button");
    cancelBtn.type = "button";
    cancelBtn.textContent = "ยกเลิกเทรด";
    cancelBtn.style.cssText = "padding: 4px 10px; background: #4a2222; border: 1px solid #823535; border-radius: 4px; color: #fff; cursor: pointer; font-size: 10px;";
    cancelBtn.onclick = () => this.callbacks.onCancel();
    ctrlBar.appendChild(cancelBtn);

    const lockBtn = document.createElement("button");
    lockBtn.type = "button";
    lockBtn.textContent = myOffer.locked ? "🔓 ปลดล็อก" : "🔒 ล็อกข้อเสนอ";
    lockBtn.style.cssText = `padding: 4px 10px; background: ${myOffer.locked ? "#555" : "#32623a"}; border: 1px solid ${myOffer.locked ? "#777" : "#519e5f"}; border-radius: 4px; color: #fff; cursor: pointer; font-size: 10px;`;
    lockBtn.onclick = () => this.callbacks.onLock(!myOffer.locked);
    ctrlBar.appendChild(lockBtn);

    const confirmBtn = document.createElement("button");
    confirmBtn.type = "button";
    const canConfirm = myOffer.locked && partnerOffer.locked;
    confirmBtn.textContent = myOffer.confirmed ? "✅ ยืนยันแล้ว (รออีกฝ่าย)" : "✅ แลกเปลี่ยน";
    confirmBtn.disabled = !canConfirm || myOffer.confirmed;
    confirmBtn.style.cssText = `padding: 4px 12px; background: ${canConfirm && !myOffer.confirmed ? "#29773b" : "#222"}; border: 1px solid ${canConfirm && !myOffer.confirmed ? "#4ac269" : "#444"}; border-radius: 4px; color: ${canConfirm ? "#fff" : "#777"}; cursor: ${canConfirm && !myOffer.confirmed ? "pointer" : "default"}; font-size: 10px; font-weight: bold;`;
    confirmBtn.onclick = () => {
      if (canConfirm && !myOffer.confirmed) {
        this.callbacks.onConfirm();
      }
    };
    ctrlBar.appendChild(confirmBtn);

    wrap.appendChild(ctrlBar);
    body.appendChild(wrap);
  }

  private renderOfferColumn(title: string, offer: TradeOffer, isMe: boolean): HTMLElement {
    const col = document.createElement("div");
    col.style.cssText = `background: rgba(255, 255, 255, 0.04); border: 1px solid ${offer.locked ? (offer.confirmed ? "#4ac269" : "#ffc107") : "rgba(255, 255, 255, 0.15)"}; border-radius: 6px; padding: 6px; display: flex; flex-direction: column; gap: 6px;`;

    // หัวคอลัมน์ + สถานะล็อก
    const header = document.createElement("div");
    header.style.cssText = "display: flex; justify-content: space-between; align-items: center;";
    const titleEl = document.createElement("span");
    titleEl.style.cssText = "font-weight: bold; color: #f0e6d2;";
    titleEl.textContent = title;
    header.appendChild(titleEl);

    const statusBadge = document.createElement("span");
    statusBadge.style.cssText = "font-size: 9px; padding: 1px 4px; border-radius: 3px;";
    if (offer.confirmed) {
      statusBadge.style.cssText += "background: #1e4620; color: #6ee7b7; border: 1px solid #34d399;";
      statusBadge.textContent = "✅ ยืนยันแล้ว";
    } else if (offer.locked) {
      statusBadge.style.cssText += "background: #4d3a0f; color: #fde047; border: 1px solid #ca8a04;";
      statusBadge.textContent = "🔒 ล็อกแล้ว";
    } else {
      statusBadge.style.cssText += "background: #2a2a2a; color: #aaa;";
      statusBadge.textContent = "🔓 ยังไม่ล็อก";
    }
    header.appendChild(statusBadge);
    col.appendChild(header);

    // รายการไอเทม
    const itemList = document.createElement("div");
    itemList.style.cssText = "min-height: 80px; max-height: 120px; overflow-y: auto; background: rgba(0,0,0,0.25); border-radius: 4px; padding: 4px; display: flex; flex-direction: column; gap: 3px;";

    const itemEntries = Object.entries(offer.items).filter(([, c]) => c > 0);
    if (itemEntries.length === 0) {
      const empty = document.createElement("div");
      empty.style.cssText = "color: #666; font-style: italic; text-align: center; margin-top: 25px; font-size: 9px;";
      empty.textContent = "(ยังไม่มีไอเทม)";
      itemList.appendChild(empty);
    } else {
      for (const [id, count] of itemEntries) {
        const itemDef = ITEMS[id];
        const row = document.createElement("div");
        row.style.cssText = "display: flex; justify-content: space-between; align-items: center; background: rgba(255,255,255,0.05); padding: 2px 4px; border-radius: 3px;";

        const nameSpan = document.createElement("span");
        nameSpan.textContent = `${itemDef?.icon ?? "📦"} ${itemDef?.name ?? id} ×${count}`;
        row.appendChild(nameSpan);

        if (isMe && !offer.locked) {
          const rmBtn = document.createElement("button");
          rmBtn.type = "button";
          rmBtn.textContent = "×";
          rmBtn.title = "เอาออก";
          rmBtn.style.cssText = "background: none; border: none; color: #ff6b6b; cursor: pointer; font-size: 11px; padding: 0 4px;";
          rmBtn.onclick = () => this.callbacks.onOfferItem(id, 0);
          row.appendChild(rmBtn);
        }
        itemList.appendChild(row);
      }
    }
    col.appendChild(itemList);

    // ส่วนโกลด์
    const goldRow = document.createElement("div");
    goldRow.style.cssText = "display: flex; justify-content: space-between; align-items: center; padding-top: 4px; border-top: 1px solid rgba(255,255,255,0.08);";

    const goldLabel = document.createElement("span");
    goldLabel.style.cssText = "color: #ffd700; font-weight: bold;";
    goldLabel.textContent = "💰 โกลด์:";
    goldRow.appendChild(goldLabel);

    if (isMe && !offer.locked) {
      const goldInput = document.createElement("input");
      goldInput.type = "number";
      goldInput.min = "0";
      goldInput.value = String(offer.gold);
      goldInput.style.cssText = "width: 70px; text-align: right; background: #1a2233; color: #ffd700; border: 1px solid rgba(255,255,255,0.2); border-radius: 4px; padding: 2px 4px; font-size: 10px;";
      goldInput.onchange = () => {
        const g = Math.max(0, parseInt(goldInput.value, 10) || 0);
        this.callbacks.onSetGold(g);
      };
      goldRow.appendChild(goldInput);
    } else {
      const goldVal = document.createElement("span");
      goldVal.style.cssText = "color: #ffd700; font-weight: bold;";
      goldVal.textContent = offer.gold.toLocaleString();
      goldRow.appendChild(goldVal);
    }
    col.appendChild(goldRow);

    return col;
  }
}
